import { useEffect, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { isEqual } from "lodash";
import { getDriverIds } from "../../../../hooks/use-driver";
import { getPartnerIds } from "../../../../hooks/use-partner";
import { CircularProgress, RCSelect } from "../../../mui";
import { useStepperContext } from "../../StepperContext";
import { DriverDetailsCards, ViewCards } from "../../components";
import { useDriverDetails } from "../../hooks/use-driver-details";

const StepTwo = ({ index }) => {
  const { data, activeStep, updateData } = useStepperContext();
  const [partnerIdList, setPartnerIdList] = useState([]);

  const { data: partnerIdListData = [], isLoading: isPartnerLoading } =
    useQuery({
      queryKey: ["partnerIds"],
      queryFn: getPartnerIds,
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    });

  useEffect(() => {
    const newPartnerIdList = partnerIdListData.map((item) => ({
      value: `${item.partnerId}/${item.deviceId}`,
      label: `${item.partnerId}/${item.deviceId}`,
    }));

    setPartnerIdList((previous) =>
      isEqual(previous, newPartnerIdList) ? previous : newPartnerIdList,
    );
  }, [partnerIdListData]);

  const { data: driverIdList = [], isLoading: isDriverListLoading } = useQuery({
    queryKey: ["driverIds"],
    queryFn: getDriverIds,
  });

  const { driverDetails, isLoading } = useDriverDetails(
    data.driverId,
    data.partnerId,
  );

  useEffect(() => {
    if (!data.partnerId && data.driverId) {
      updateData("driverId", "");
      updateData("driverDetails", null);
      updateData("isDriverDetails", false);
    }
  }, [data.partnerId, data.driverId, updateData]);

  useEffect(() => {
    if (!data.driverId || !data.partnerId || !driverDetails) {
      return;
    }

    const isValidDriver = driverDetails.driverId === data.driverId;

    if (data.isDriverDetails !== isValidDriver) {
      updateData("isDriverDetails", isValidDriver);
    }

    if (isValidDriver && !isEqual(driverDetails, data.driverDetails)) {
      updateData("driverDetails", driverDetails);
    }
  }, [
    driverDetails,
    data.driverId,
    data.partnerId,
    data.isDriverDetails,
    data.driverDetails,
    updateData,
  ]);

  const handleSelectedPartnerData = (selectedPartner) => {
    const value = selectedPartner?.value || "";
    const [partnerId = "", deviceId = ""] = value.split("/");

    if (partnerId === data.partnerId && deviceId === data.deviceId) {
      return;
    }

    const partnerDetails =
      partnerIdListData.find(
        (partner) =>
          String(partner.partnerId) === partnerId &&
          String(partner.deviceId) === deviceId,
      ) || {};

    updateData("isDriverDetails", false);
    updateData("driverDetails", null);
    updateData("driverId", "");
    updateData("partnerId", partnerId);
    updateData("deviceId", deviceId);
    updateData("partnerDetails", partnerDetails);
  };

  const handleSelectedDriver = (selectedOption) => {
    const driverId = selectedOption?.value || "";

    if (driverId === data.driverId) {
      return;
    }

    updateData("isDriverDetails", false);
    updateData("driverDetails", null);
    updateData("driverId", driverId);
  };

  return (
    <Stack gap={3}>
      <Box>
        <RCSelect
          label="Partner Details"
          options={partnerIdList}
          value={
            partnerIdList.find(
              (option) => option.value === `${data.partnerId}/${data.deviceId}`,
            ) || null
          }
          onChange={handleSelectedPartnerData}
          isLoading={isPartnerLoading}
          isDisabled={activeStep > index || data?.swapInitiated}
        />

        {data.partnerId &&
        data.deviceId &&
        data.deviceId !== "null" &&
        data.partnerDetails ? (
          <ViewCards view="partnerDetails" />
        ) : (
          data.partnerId && (
            <Box sx={{ my: 2 }}>
              <Typography sx={{ color: "#F32424" }}>
                Station not assigned to the partner!
              </Typography>
            </Box>
          )
        )}
      </Box>

      <Box>
        <RCSelect
          label="Driver ID Details"
          options={driverIdList}
          value={
            driverIdList.find((option) => option.value === data.driverId) ||
            null
          }
          onChange={handleSelectedDriver}
          isLoading={isDriverListLoading || isLoading}
          isDisabled={
            !data.partnerId ||
            !data.deviceId ||
            data.deviceId === "null" ||
            activeStep > index ||
            data?.swapInitiated
          }
        />

        {data.driverId && data.partnerId ? (
          !isLoading ? (
            <DriverDetailsCards driverData={driverDetails} />
          ) : (
            <CircularProgress />
          )
        ) : null}
      </Box>
    </Stack>
  );
};

export default StepTwo;
